import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowDownToLine,
    ArrowUpRight,
    Wallet as WalletIcon,
    PlusCircle,
    Banknote,
    ReceiptText,
    Lock,
    KeyRound,
    ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/screens/_shared";
import {
    getPayoutMethods,
    getTransactions,
    getWallet,
    seedWalletOnce,
    hasPaymentPin,
    getTwoFactorEnabled,
    setTwoFactorEnabled,
} from "@/lib/monetizationStore";
import { useMoneVersion, timeAgo } from "@/lib/hooks";
import { useEffect } from "react";
import PinSheet from "@/components/PinSheet";

const fmt = (n) => "₦" + Number(n || 0).toLocaleString();

const Wallet = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const v = useMoneVersion();

    useEffect(() => {
        if (user) seedWalletOnce(user.id);
    }, [user]);

    const wallet = useMemo(() => (user ? getWallet(user.id) : { available: 0, pending: 0, total: 0 }), [user, v]);
    const txns = useMemo(() => (user ? getTransactions(user.id).slice(0, 6) : []), [user, v]);
    const payouts = useMemo(() => (user ? getPayoutMethods(user.id) : []), [user, v]);
    const pinSet = useMemo(() => (user ? hasPaymentPin(user.id) : false), [user, v]);
    const twoFA = useMemo(() => (user ? getTwoFactorEnabled(user.id) : false), [user, v]);
    const [pinMode, setPinMode] = useState(null); // null | "set" | "verify" | "reset"

    const handlePinSheetClose = (opts) => {
        if (opts && opts.forgot) {
            setPinMode("reset");
            return;
        }
        setPinMode(null);
    };

    return (
        <div className="screen" data-testid="wallet-screen">
            <PageHeader title="Wallet" />
            <div className="flex-1 overflow-y-auto no-scrollbar px-5 py-5">
                {/* Balance card */}
                <div
                    className="rounded-3xl p-5 text-primary-foreground shadow-card"
                    style={{
                        background:
                            "linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary) / 0.72) 100%)",
                    }}
                >
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] font-semibold text-white/80">
                        <WalletIcon size={13} />
                        Total balance
                    </div>
                    <div className="mt-2 font-display text-[32px] font-semibold leading-tight" data-testid="wallet-total">
                        {fmt(wallet.total)}
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-3 text-[12px]">
                        <div>
                            <div className="text-white/75 text-[10.5px] uppercase tracking-wider">Available</div>
                            <div className="font-display text-[15px] font-semibold" data-testid="wallet-available">
                                {fmt(wallet.available)}
                            </div>
                        </div>
                        <div>
                            <div className="text-white/75 text-[10.5px] uppercase tracking-wider">Pending</div>
                            <div className="font-display text-[15px] font-semibold" data-testid="wallet-pending">
                                {fmt(wallet.pending)}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="mt-4 grid grid-cols-2 gap-3">
                    <ActionTile
                        testId="wallet-withdraw-btn"
                        icon={ArrowUpRight}
                        label="Withdraw"
                        onClick={() => navigate("/wallet/withdraw")}
                    />
                    <ActionTile
                        testId="wallet-payouts-btn"
                        icon={Banknote}
                        label="Payout Methods"
                        onClick={() => navigate("/wallet/payouts")}
                    />
                </div>

                {/* Transactions */}
                <div className="mt-6 flex items-center justify-between px-1">
                    <h3 className="font-display text-[15px] font-semibold">Transactions</h3>
                    <button
                        data-testid="wallet-see-all-txns"
                        onClick={() => navigate("/wallet/transactions")}
                        className="text-[12.5px] font-medium text-primary"
                    >
                        See all
                    </button>
                </div>
                <ul className="mt-2 space-y-1.5" data-testid="wallet-tx-list">
                    {txns.length === 0 ? (
                        <li className="text-[12.5px] text-muted-foreground text-center py-6">
                            No transactions yet. Your earnings and payments will appear here.
                        </li>
                    ) : (
                        txns.map((t) => <TxnRow key={t.id} txn={t} />)
                    )}
                </ul>

                {/* Quick payouts summary */}
                {payouts.length > 0 && (
                    <div className="mt-6">
                        <h3 className="font-display text-[15px] font-semibold mb-2">Primary payout</h3>
                        {payouts
                            .filter((p) => p.isPrimary)
                            .map((p) => (
                                <div
                                    key={p.id}
                                    className="flex items-center gap-3 p-3 rounded-2xl border border-border bg-card"
                                >
                                    <span
                                        className="h-10 w-10 rounded-xl flex items-center justify-center text-white font-semibold text-[11px]"
                                        style={{ background: p.bankColor }}
                                    >
                                        {p.bankName.slice(0, 2).toUpperCase()}
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-[13px] font-semibold truncate">{p.accountName}</div>
                                        <div className="text-[11.5px] text-muted-foreground truncate">
                                            {p.bankName} · {p.accountNumber.replace(/(\d{3})(\d{3})(\d{4})/, "$1 $2 $3")}
                                        </div>
                                    </div>
                                </div>
                            ))}
                    </div>
                )}

                {/* Security */}
                <div className="mt-6" data-testid="wallet-security-section">
                    <h3 className="font-display text-[15px] font-semibold mb-2">Security</h3>
                    <div className="space-y-2">
                        <SecurityRow
                            icon={Lock}
                            label={pinSet ? "Change PIN" : "Set payment PIN"}
                            caption={pinSet ? "Update your 4-digit PIN" : "Required for withdrawals"}
                            onClick={() => setPinMode(pinSet ? "verify" : "set")}
                            testId="wallet-security-change-pin"
                        />
                        <SecurityRow
                            icon={KeyRound}
                            label="Reset PIN via OTP"
                            caption="Forgot your PIN?"
                            onClick={() => setPinMode("reset")}
                            disabled={!pinSet}
                            testId="wallet-security-reset-pin"
                        />
                        <SecurityToggleRow
                            icon={ShieldCheck}
                            label="Enable 2FA"
                            caption="Extra confirmation for sensitive actions"
                            checked={twoFA}
                            onChange={(next) => user && setTwoFactorEnabled(user.id, next)}
                            testId="wallet-security-2fa"
                        />
                    </div>
                </div>

                <div className="h-8" />
            </div>

            {pinMode && user && (
                <PinSheet
                    open={!!pinMode}
                    onClose={handlePinSheetClose}
                    userId={user.id}
                    mode={
                        // "Change PIN" with existing PIN: verify first, then on success switch to set.
                        // We use reset (with OTP) for forgot, set when no PIN exists.
                        pinMode === "verify" && pinSet ? "verify" :
                        pinMode === "reset" ? "reset" :
                        "set"
                    }
                    onSuccess={() => {
                        // After verifying current PIN, transition to setting a new one
                        if (pinMode === "verify") {
                            setPinMode("set");
                            return;
                        }
                        setPinMode(null);
                    }}
                />
            )}
        </div>
    );
};

const SecurityRow = ({ icon: Icon, label, caption, onClick, disabled, testId }) => (
    <button
        type="button"
        data-testid={testId}
        onClick={onClick}
        disabled={disabled}
        className={[
            "w-full flex items-center gap-3 p-3 rounded-2xl border border-border bg-card text-left transition-colors",
            disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-accent/40 active:scale-[0.99]",
        ].join(" ")}
    >
        <span className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Icon size={16} />
        </span>
        <span className="flex-1 min-w-0">
            <span className="block text-[13.5px] font-semibold truncate">{label}</span>
            <span className="block text-[11.5px] text-muted-foreground truncate">{caption}</span>
        </span>
    </button>
);

const SecurityToggleRow = ({ icon: Icon, label, caption, checked, onChange, testId }) => (
    <button
        type="button"
        data-testid={testId}
        onClick={() => onChange?.(!checked)}
        className="w-full flex items-center gap-3 p-3 rounded-2xl border border-border bg-card text-left hover:bg-accent/40 active:scale-[0.99] transition-colors"
        aria-pressed={checked}
    >
        <span className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Icon size={16} />
        </span>
        <span className="flex-1 min-w-0">
            <span className="block text-[13.5px] font-semibold truncate">{label}</span>
            <span className="block text-[11.5px] text-muted-foreground truncate">{caption}</span>
        </span>
        <span
            className={["relative h-6 w-10 rounded-full transition-colors shrink-0", checked ? "bg-primary" : "bg-muted"].join(" ")}
            aria-hidden
        >
            <span
                className={["absolute top-0.5 h-5 w-5 rounded-full bg-background shadow transition-all", checked ? "left-[18px]" : "left-0.5"].join(" ")}
            />
        </span>
    </button>
);

const ActionTile = ({ icon: Icon, label, onClick, testId }) => (
    <button
        data-testid={testId}
        onClick={onClick}
        className="flex items-center gap-2 p-3.5 rounded-2xl border border-border bg-card shadow-soft active:scale-[0.99] transition-transform"
    >
        <span className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
            <Icon size={18} strokeWidth={2.3} />
        </span>
        <span className="text-[13.5px] font-semibold text-left">{label}</span>
    </button>
);

const KIND_ICON = {
    service:  PlusCircle,
    event:    PlusCircle,
    withdraw: ArrowUpRight,
    fee:      ReceiptText,
    boost:    PlusCircle,
    premium:  PlusCircle,
    verification: PlusCircle,
};

const TxnRow = ({ txn }) => {
    const Icon = KIND_ICON[txn.kind] || ReceiptText;
    const credit = txn.type === "credit";
    return (
        <li
            data-testid={`tx-row-${txn.id}`}
            className="flex items-center gap-3 p-3 rounded-2xl hover:bg-accent/30"
        >
            <span
                className={[
                    "h-10 w-10 rounded-xl flex items-center justify-center",
                    credit ? "bg-primary/10 text-primary" : "bg-destructive/10 text-destructive",
                ].join(" ")}
            >
                <Icon size={16} />
            </span>
            <div className="min-w-0 flex-1">
                <div className="text-[13.5px] font-medium truncate">{txn.title}</div>
                <div className="text-[11px] text-muted-foreground">{timeAgo(txn.at)}</div>
            </div>
            <div
                className={[
                    "font-display text-[13.5px] font-semibold",
                    credit ? "text-emerald-600 dark:text-emerald-400" : "",
                ].join(" ")}
            >
                {credit ? "+" : "−"}{fmt(txn.amount)}
            </div>
        </li>
    );
};

export default Wallet;
